import type {
  MollieJournalCheck,
  MollieMonthFees,
  ReconciliationPeriod,
  ReconciliationWarning,
} from '../../../types/models/Reconciliation';
import { formatBareDateNumeric } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';

// Turns a response's `warnings` (plus the period / self-check / fees it describes) into the banners the
// accountant must see, in French, most severe first. A partial view says so — it never fails silently: an
// unknown warning code still renders (with the backend's own message).

export type BannerVariant = 'danger' | 'warning' | 'info';

export interface ReconciliationBanner {
  code: string;
  variant: BannerVariant;
  title: string;
  body: string | null;
  /** Offer a retry (Actualiser) — the view is partial and a second read may complete it. */
  retry: boolean;
}

export interface BannerContext {
  period?: ReconciliationPeriod | null;
  check?: MollieJournalCheck | null;
  fees?: MollieMonthFees | null;
  /** The accounting-entries view: several warnings mean something specific there. */
  view?: 'entries';
  /** Accounting entries: `false` when a source was skipped or cut short. */
  complete?: boolean;
  /** Accounting entries: the proof on the shown cents (anything but `{true, 0}` is a backend bug). */
  entriesCheck?: { balanced: boolean; unbalanced_entries: number } | null;
}

/** The accounting-entries meaning of a warning, or null when it reads as in the other views. */
function entriesBannerFor(warning: ReconciliationWarning, ctx: BannerContext): ReconciliationBanner | null {
  const base = { code: warning.code, body: null, retry: false };
  switch (warning.code) {
    case 'entries_unbalanced': {
      const n = ctx.entriesCheck?.unbalanced_entries ?? 0;
      return {
        ...base,
        variant: 'danger',
        title: 'Écritures déséquilibrées : anomalie à signaler',
        body: `${n} ${pluralize(n, 'écriture déséquilibrée', 'écritures déséquilibrées')}, ou un total débit différent du total crédit. Cela ne devrait jamais arriver : signalez-le avant d'encoder.`,
      };
    }
    case 'entries_incomplete':
      return {
        ...base,
        variant: 'warning',
        title: 'Vue incomplète : une source manque',
        body: 'Les avertissements ci-dessous indiquent laquelle. Ne vous fiez pas à cette vue pour clôturer le mois.',
      };
    case 'period_settling':
      return {
        ...base,
        variant: 'warning',
        title: ctx.period
          ? `Mois provisoire — définitif le ${formatBareDateNumeric(ctx.period.final_after)}`
          : 'Mois provisoire',
        body: "La facture Mollie du mois et des documents Facturier tardifs peuvent encore y apparaître. Les versements sont datés du jour du versement : les paiements par carte tardifs n'entrent pas dans ce mois.",
      };
    case 'mollie_unavailable':
      return {
        ...base,
        variant: 'warning',
        title: 'Mollie indisponible : écritures Mollie absentes ou incomplètes',
        body: `Si Mollie n'est pas configuré ou en erreur, aucune écriture de versement ni de facture Mollie n'est affichée. Si seul l'enrichissement Mollie du journal des ventes a échoué, les écritures sont là, mais les textes de répartition des ventes n'ont pas le détail des versements.${warning.message ? ` Cause : ${warning.message}` : ''}`,
      };
    case 'facturier_unavailable':
      return {
        ...base,
        variant: 'warning',
        title: 'Facturier indisponible : aucune écriture de vente',
        body: "Les documents de vente n'ont pas pu être lus.",
      };
    case 'facturier_lookups_capped':
      return {
        ...base,
        variant: 'warning',
        title: 'Certains numéros de facture des commandes de domaine manquent dans les textes',
        body: 'Les écritures sont complètes.',
      };
    case 'time_budget_exhausted':
      return {
        ...base,
        variant: 'warning',
        title: 'Vue partielle : la lecture a manqué de temps',
        body: 'Ce qui a été lu entièrement est affiché ; selon la source interrompue, des écritures Mollie ou de vente peuvent manquer. Actualisez pour réessayer.',
        retry: true,
      };
    default:
      return null;
  }
}

const SEVERITY: Record<BannerVariant, number> = { danger: 0, warning: 1, info: 2 };

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function bannerFor(warning: ReconciliationWarning, ctx: BannerContext): ReconciliationBanner {
  const specific = ctx.view === 'entries' ? entriesBannerFor(warning, ctx) : null;
  if (specific !== null) {
    return specific;
  }
  const { period, check, fees } = ctx;
  const base = { code: warning.code, body: null, retry: false };
  switch (warning.code) {
    case 'journal_mismatch':
      return {
        ...base,
        variant: 'danger',
        title: 'Le journal ne correspond pas au rapport de solde Mollie',
        body: check
          ? `Somme des mouvements ${formatPrice(check.transactions_net)} ≠ variation du solde Mollie ${formatPrice(check.report_change)} (écart ${formatPrice(check.difference)}). Un mouvement manque ou est en trop : ne clôturez pas ce mois.`
          : 'Un mouvement manque ou est en trop : ne clôturez pas ce mois.',
      };
    case 'fees_mismatch':
      return {
        ...base,
        variant: 'danger',
        title: 'La facture Mollie ne correspond pas aux frais retenus par les versements',
        body:
          fees && fees.invoiced !== null && fees.difference !== null
            ? `Facturé ${formatPrice(fees.invoiced)} ≠ retenu net ${formatPrice(fees.settled_total)} (écart ${formatPrice(fees.difference)}).`
            : null,
      };
    case 'period_settling':
      return {
        ...base,
        variant: 'warning',
        title: period
          ? `Mois provisoire — définitif le ${formatBareDateNumeric(period.final_after)}`
          : 'Mois provisoire',
        body: "Les paiements par carte des derniers jours du mois peuvent encore arriver sur le solde Mollie et y être comptabilisés. Ne clôturez ce mois qu'une fois définitif.",
      };
    case 'payments_not_yet_booked': {
      const pending = check?.pending_payments;
      return {
        ...base,
        variant: 'warning',
        title: pending
          ? `${pending.count} ${pluralize(pending.count, "paiement payé ce mois-ci n'est", 'paiements payés ce mois-ci ne sont')} pas encore sur le solde Mollie`
          : 'Des paiements payés ce mois-ci ne sont pas encore sur le solde Mollie',
        body: pending
          ? `${formatPrice(pending.gross)} au total, absents du journal pour l'instant : le mois va encore changer.`
          : "Ils sont absents du journal pour l'instant : le mois va encore changer.",
      };
    }
    case 'time_budget_exhausted':
      return {
        ...base,
        variant: 'warning',
        title: 'Vue partielle : la lecture de Mollie a manqué de temps',
        body: "Toutes les données n'ont pas pu être lues dans le temps imparti. Actualisez pour réessayer.",
        retry: true,
      };
    case 'period_partial':
      return {
        ...base,
        variant: 'info',
        title:
          period && period.last_day
            ? `Mois en cours — provisoire, affiché jusqu'au ${formatBareDateNumeric(period.last_day)} inclus`
            : 'Mois en cours — provisoire',
        body: "La journée d'aujourd'hui n'est pas encore disponible chez Mollie. Ne clôturez ce mois qu'une fois définitif.",
      };
    case 'period_not_started':
      return {
        ...base,
        variant: 'info',
        title: 'Premier jour du mois : aucune journée complète à afficher',
      };
    case 'mollie_transactions_truncated':
    case 'mollie_payments_truncated':
    case 'mollie_index_truncated':
      return {
        ...base,
        variant: 'warning',
        title: 'Vue incomplète : une limite de pages Mollie a été atteinte',
      };
    case 'mollie_payment_lookups_capped':
    case 'mollie_payment_missing':
      return {
        ...base,
        variant: 'warning',
        title: "Certains détails de paiement, de remboursement ou de rétrofacturation n'ont pas pu être lus",
      };
    case 'mollie_unavailable':
      return {
        ...base,
        variant: 'warning',
        title: 'Mollie indisponible : les colonnes Mollie sont vides',
        body: "Le reporting Mollie n'est pas configuré ou est en erreur ; le journal des ventes est affiché sans le détail des paiements et des versements.",
      };
    case 'unknown_transaction_type':
      return {
        ...base,
        variant: 'warning',
        title: 'Mollie a renvoyé un type de mouvement non documenté (affiché tel quel)',
      };
    case 'facturier_unavailable':
    case 'facturier_lookups_capped':
      return {
        ...base,
        variant: 'warning',
        title: 'Certains numéros de facture des commandes de domaine manquent',
        body: "Facturier n'a pas pu être lu entièrement.",
      };
    case 'sales_journal_truncated':
      return {
        ...base,
        variant: 'warning',
        title: 'Plus de 10 000 documents : la liste est incomplète',
      };
    case 'mollie_payout_unexplained':
      return {
        ...base,
        variant: 'warning',
        title: "Un versement Mollie n'est pas entièrement expliqué par ses lignes",
        body: "L'écart est en compte d'attente (signalement « Écart inexpliqué »).",
      };
    case 'winauditor_customer_codes_missing':
      return {
        ...base,
        variant: 'warning',
        title: 'Codes clients WinAuditor manquants : certaines OD ne peuvent pas être exportées',
        body: 'Saisissez-les dans « Codes clients WinAuditor » : les OD signalées « Code client WinAuditor manquant » pourront ensuite être exportées. Les écritures du mois sont complètes.',
      };
    case 'facturier_accounting_fields_missing':
      return {
        ...base,
        variant: 'warning',
        title: "Facturier n'envoie pas encore les champs comptables de certains documents",
        body: 'Ces écritures sont estimées (signalements « Ventilation estimée » / « Statut WinAuditor inconnu ») : déployez la version de Facturier qui les envoie.',
      };
    default:
      return { ...base, variant: 'warning', title: warning.message || warning.code };
  }
}

/**
 * The banners for one response, most severe first, one per code. The period / self-check / fees state is
 * also read directly, so a prominent condition still shows if its warning were ever missing.
 */
export function buildBanners(warnings: ReconciliationWarning[], ctx: BannerContext = {}): ReconciliationBanner[] {
  const all = [...warnings];
  const has = (code: string) => all.some((w) => w.code === code);
  const ensure = (condition: boolean | undefined, code: string) => {
    if (condition && !has(code)) {
      all.push({ code, message: '' });
    }
  };
  ensure(ctx.check?.ok === false, 'journal_mismatch');
  ensure(ctx.fees?.ok === false, 'fees_mismatch');
  ensure(ctx.period?.settling, 'period_settling');
  ensure(ctx.period?.partial, 'period_partial');
  ensure((ctx.check?.pending_payments.count ?? 0) > 0, 'payments_not_yet_booked');
  ensure(ctx.entriesCheck?.balanced === false || (ctx.entriesCheck?.unbalanced_entries ?? 0) > 0, 'entries_unbalanced');
  // First among the warnings: the source-specific warnings below it say what is missing.
  if (ctx.complete === false && !has('entries_incomplete')) {
    all.unshift({ code: 'entries_incomplete', message: '' });
  }

  const seen = new Set<string>();
  const banners: ReconciliationBanner[] = [];
  for (const w of all) {
    if (seen.has(w.code)) {
      continue;
    }
    seen.add(w.code);
    banners.push(bannerFor(w, ctx));
  }
  return banners.sort((a, b) => SEVERITY[a.variant] - SEVERITY[b.variant]);
}
